import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, shareReplay, tap} from 'rxjs/operators';
import {environment} from '../../../../../../environments/environment';
import {
    I18nConversionConfig, I18nConversionField, I18nConversionGroup,
    I18nConversionLanguage, I18nConversionPreview
} from './i18n-conversion.interface';

interface GroupDescriptor {
    group: string;
    fields: I18nConversionField[];
    languages: I18nConversionLanguage[];
    requiresLanguage: boolean;
}

@Injectable({providedIn: 'root'})
export class EtlI18nConversionService {
    readonly noMatchPolicies = ['ERROR', 'EMPTY', 'KEEP_SOURCE'] as const;
    private readonly baseUrl = environment.SERVER_API_URL + 'services/configurator/api/i18ns/conversion';
    private readonly catalogs = new Map<string, I18nConversionGroup[]>();
    private readonly requests = new Map<string, Observable<I18nConversionGroup[]>>();

    constructor(private http: HttpClient) {}

    loadGroups(tenant: string, reload = false): Observable<I18nConversionGroup[]> {
        if (reload) {
            this.requests.delete(tenant);
            this.catalogs.delete(tenant);
        }
        if (!this.requests.has(tenant)) {
            const request = this.http.get<GroupDescriptor[]>(`${this.baseUrl}/groups`, {headers: this.headers(tenant)})
                .pipe(
                    // Descriptors are small; dictionary documents stay on the server.
                    // Labels remain local so changing the UI language doesn't request the catalog again.
                    map(descriptors => descriptors.map(group => this.toGroup(group))),
                    tap(groups => this.catalogs.set(tenant, groups)),
                    shareReplay({bufferSize: 1, refCount: true})
                );
            this.requests.set(tenant, request);
        }
        return this.requests.get(tenant);
    }

    getGroup(tenant: string, group: string): I18nConversionGroup | undefined {
        return this.catalogs.get(tenant)?.find(item => item.value === group);
    }

    needsLanguage(tenant: string, config: I18nConversionConfig): boolean {
        const group = this.getGroup(tenant, config?.group);
        return !!group && (group.requiresLanguage || group.fields.some(field =>
            field.translated && (field.value === config.matchField || field.value === config.resultField)));
    }

    isValid(tenant: string, config: I18nConversionConfig): boolean {
        const group = this.getGroup(tenant, config?.group);
        return !!group && group.fields.some(field => field.value === config.matchField) &&
            group.fields.some(field => field.value === config.resultField) &&
            typeof config.onlyEnabled === 'boolean' && this.noMatchPolicies.includes(config.onNoMatch) &&
            (!this.needsLanguage(tenant, config) || group.languages.includes(config.language));
    }

    normalize(tenant: string, config: I18nConversionConfig): I18nConversionConfig {
        const normalized = {...config, onlyEnabled: false};
        if (this.getGroup(tenant, normalized.group) && !this.needsLanguage(tenant, normalized)) delete normalized.language;
        return normalized;
    }

    preview(tenant: string, config: I18nConversionConfig, sources: string[]): Observable<I18nConversionPreview[]> {
        return this.http.post<I18nConversionPreview[]>(`${this.baseUrl}/preview`,
            {configuration: this.normalize(tenant, config), sources}, {headers: this.headers(tenant)});
    }

    private headers(tenant: string): HttpHeaders { return new HttpHeaders().set('X-DataStoreID', tenant); }

    private toGroup(descriptor: GroupDescriptor): I18nConversionGroup {
        const names = {CITY: 'city', AIRPORT: 'airport', COUNTRY: 'country', common: 'common', DIAL_CODE: 'dialCode'};
        const fieldNames = {
            CITY: {key: 'cityKey', value: 'cityName'},
            AIRPORT: {key: 'airportKey', value: 'airportName', cityCode: 'cityKey'},
            COUNTRY: {key: 'countryCode', value: 'countryName'}
        };
        return {
            value: descriptor.group,
            label: names[descriptor.group] ? `etlI18nConversion.groups.${names[descriptor.group]}` : descriptor.group,
            fields: descriptor.fields.map(value => ({value,
                label: `etlI18nConversion.fields.${fieldNames[descriptor.group]?.[value] || value}`,
                translated: value === 'value'})),
            defaultMatchField: descriptor.fields.includes('key') ? 'key' : descriptor.fields[0],
            languages: descriptor.languages, requiresLanguage: descriptor.requiresLanguage
        };
    }
}
